"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import type { ProjectTypeCode } from "@agroproj/backend/convex/constants/projectTypes";
import { getProjectTypeLabel } from "@agroproj/backend/convex/constants/projectTypes";
import { useQuery } from "convex/react";
import { Search, Wheat } from "lucide-react";
import { useMemo, useState } from "react";
import { LoadingPage } from "@/components/loading-page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { RadioGroup } from "@/components/ui/radio-group";
import { dedupeActivityOptions } from "@/lib/activity-options";
import { EMPTY_VALUE } from "@/lib/format";
import { ProjectFormRadioOption } from "./project-form-radio-option";
type ActivitySelectionDialogProps = {
  projectType: ProjectTypeCode | undefined;
  value: Id<"base_activities"> | undefined;
  onChange: (value: Id<"base_activities">) => void;
  disabled?: boolean;
  /** Id for the trigger button so an external `Label` can point at it. */
  triggerId?: string;
};
export function ActivitySelectionDialog(t0) {
  const $ = _c(73);
  const {
    projectType,
    value,
    onChange,
    disabled: t1,
    triggerId
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const [open, setOpen] = useState(false);
  let t2;
  if ($[0] !== projectType) {
    t2 = {
      projectType,
      searchTerm: "",
      page: 0
    };
    $[0] = projectType;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [filters, setFilters] = useState(t2);
  const filtersMatchProjectType = filters.projectType === projectType;
  const searchTerm = filtersMatchProjectType ? filters.searchTerm : "";
  const page = filtersMatchProjectType ? filters.page : 0;
  let t3;
  if ($[2] !== projectType) {
    t3 = next => {
      setFilters(current => ({
        projectType,
        searchTerm: current.projectType === projectType ? current.searchTerm : "",
        page: current.projectType === projectType ? current.page : 0,
        ...next
      }));
    };
    $[2] = projectType;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const updateFilters = t3;
  const offset = page * 20;
  let t4;
  if ($[4] !== projectType) {
    t4 = {
      projectType
    };
    $[4] = projectType;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const allActivities = useQuery(api.base.queries.getBaseActivities, t4);
  let t5;
  bb0: {
    if (!allActivities) {
      let t6;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          activities: [],
          totalCount: 0,
          totalPages: 0,
          hasMore: false,
          selectedActivity: undefined
        };
        $[6] = t6;
      } else {
        t6 = $[6];
      }
      t5 = t6;
      break bb0;
    }
    let hasMore;
    let paginatedActivities;
    let t6;
    let totalCount;
    let totalPages;
    if ($[7] !== allActivities || $[8] !== offset || $[9] !== searchTerm || $[10] !== value) {
      const availableActivities = dedupeActivityOptions(allActivities);
      const filtered = searchTerm ? availableActivities.filter(activity => activity.title?.toLowerCase().includes(searchTerm.toLowerCase())) : availableActivities;
      const start = offset;
      const end = start + 20;
      paginatedActivities = filtered.slice(start, end);
      totalCount = filtered.length;
      totalPages = Math.ceil(totalCount / 20);
      hasMore = end < totalCount;
      t6 = value ? availableActivities.find(activity_0 => activity_0._id === value) : undefined;
      $[7] = allActivities;
      $[8] = offset;
      $[9] = searchTerm;
      $[10] = value;
      $[11] = hasMore;
      $[12] = paginatedActivities;
      $[13] = t6;
      $[14] = totalCount;
      $[15] = totalPages;
    } else {
      hasMore = $[11];
      paginatedActivities = $[12];
      t6 = $[13];
      totalCount = $[14];
      totalPages = $[15];
    }
    const selectedActivity = t6;
    let t7;
    if ($[16] !== hasMore || $[17] !== paginatedActivities || $[18] !== selectedActivity || $[19] !== totalCount || $[20] !== totalPages) {
      t7 = {
        activities: paginatedActivities,
        totalCount,
        totalPages,
        hasMore,
        selectedActivity
      };
      $[16] = hasMore;
      $[17] = paginatedActivities;
      $[18] = selectedActivity;
      $[19] = totalCount;
      $[20] = totalPages;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    t5 = t7;
  }
  const {
    activities,
    totalCount: totalCount_0,
    totalPages: totalPages_0,
    hasMore: hasMore_0,
    selectedActivity: selectedActivity_0
  } = t5;
  const isLoading = allActivities === undefined;
  let t6;
  if ($[22] !== onChange) {
    t6 = activityId => {
      onChange(activityId);
      setOpen(false);
    };
    $[22] = onChange;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const handleSelect = t6;
  let t7;
  if ($[24] !== updateFilters) {
    t7 = nextValue => {
      updateFilters({
        searchTerm: nextValue,
        page: 0
      });
    };
    $[24] = updateFilters;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const handleSearchChange = t7;
  const canGoPrevious = page > 0;
  const canGoNext = hasMore_0;
  const t8 = disabled || !projectType;
  let t9;
  if ($[26] !== t8 || $[27] !== triggerId) {
    t9 = <Button variant="outline" className="w-full justify-between" disabled={t8} id={triggerId} />;
    $[26] = t8;
    $[27] = triggerId;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const t10 = selectedActivity_0?.title || "Selecione uma atividade";
  let t11;
  if ($[29] !== t10) {
    t11 = <span className="truncate">{t10}</span>;
    $[29] = t10;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Wheat className="size-4 opacity-50" />;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11 || $[33] !== t9) {
    t13 = <DialogTrigger render={t9}>{t11}{t12}</DialogTrigger>;
    $[32] = t11;
    $[33] = t9;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <DialogHeader><DialogTitle className="flex items-center gap-2"><Wheat className="size-5" />Selecionar cultura/atividade</DialogTitle><DialogDescription>Escolha a cultura ou atividade para o projeto</DialogDescription></DialogHeader>;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== handleSearchChange) {
    t15 = e => handleSearchChange(e.target.value);
    $[36] = handleSearchChange;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== searchTerm || $[39] !== t15) {
    t16 = <div className="mb-4"><Input leftIcon={Search} placeholder="Buscar por nome da atividade..." value={searchTerm} onChange={t15} /></div>;
    $[38] = searchTerm;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== isLoading) {
    t17 = isLoading ? <div className="py-8"><LoadingPage /></div> : null;
    $[41] = isLoading;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== activities || $[44] !== isLoading || $[45] !== projectType || $[46] !== searchTerm) {
    t18 = !isLoading && activities.length === 0 ? <EmptyState size="sm" title={searchTerm ? "Nenhuma atividade encontrada com este termo de busca" : projectType ? "Nenhuma atividade cadastrada para este tipo de projeto" : "Selecione um tipo de projeto primeiro"} /> : null;
    $[43] = activities;
    $[44] = isLoading;
    $[45] = projectType;
    $[46] = searchTerm;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== activities || $[49] !== handleSelect || $[50] !== isLoading || $[51] !== value) {
    t19 = !isLoading && activities.length > 0 ? <RadioGroup className="flex flex-col gap-2" onValueChange={handleSelect} value={value ?? undefined}>{activities.map(_temp)}</RadioGroup> : null;
    $[48] = activities;
    $[49] = handleSelect;
    $[50] = isLoading;
    $[51] = value;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t17 || $[54] !== t18 || $[55] !== t19) {
    t20 = <div className="min-h-0 flex-1 overflow-auto">{t17}{t18}{t19}</div>;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== canGoNext || $[58] !== canGoPrevious || $[59] !== offset || $[60] !== page || $[61] !== totalCount_0 || $[62] !== totalPages_0 || $[63] !== updateFilters) {
    t21 = totalCount_0 > 0 ? <div className="flex items-center justify-between px-2 py-4"><div className="text-muted-foreground flex-1 tabular-nums">Mostrando {offset + 1} a {Math.min(offset + 20, totalCount_0)} de {totalCount_0}{" "}atividades</div><Pagination currentPage={page + 1} hasNextPage={canGoNext} hasPreviousPage={canGoPrevious} onPageChange={nextPage => updateFilters({
        page: nextPage - 1
      })} showFirstLast={true} totalPages={totalPages_0} /></div> : null;
    $[57] = canGoNext;
    $[58] = canGoPrevious;
    $[59] = offset;
    $[60] = page;
    $[61] = totalCount_0;
    $[62] = totalPages_0;
    $[63] = updateFilters;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== t16 || $[66] !== t20 || $[67] !== t21) {
    t22 = <DialogContent className="flex max-h-[80vh] w-full max-w-4xl flex-col overflow-hidden">{t14}<div className="flex min-h-0 flex-1 flex-col">{t16}{t20}{t21}</div></DialogContent>;
    $[65] = t16;
    $[66] = t20;
    $[67] = t21;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  let t23;
  if ($[69] !== open || $[70] !== t13 || $[71] !== t22) {
    t23 = <Dialog open={open} onOpenChange={setOpen}>{t13}{t22}</Dialog>;
    $[69] = open;
    $[70] = t13;
    $[71] = t22;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  return t23;
}
function _temp(activity_1) {
  const activityId_0 = `activity-${activity_1._id}`;
  return <ProjectFormRadioOption id={activityId_0} key={activity_1._id} value={activity_1._id}><div className="flex min-w-0 flex-1 items-center justify-between gap-4"><span className="truncate font-medium">{activity_1.title}</span><span className="text-muted-foreground shrink-0">{activity_1.projectTypeInfo?.title || (activity_1.projectType ? getProjectTypeLabel(activity_1.projectType) : EMPTY_VALUE)}</span></div></ProjectFormRadioOption>;
}